import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ListPageLayout } from "@dashboard/components/Layouts";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import {
  useWsmFitmentApplicationBulkCreateMutation,
  useWsmFitmentApplicationDeleteMutation,
  useWsmFitmentApplicationsQuery,
  useWsmFitmentAxisValueBulkCreateMutation,
  useWsmFitmentQualifierValueBulkCreateMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { WsmFitmentApplicationDraft } from "../components/WsmFitmentApplicationAddDialog";
import WsmFitmentApplicationsPage from "../components/WsmFitmentApplicationsPage";
import {
  useWsmFitmentEmptyPageFallback,
  useWsmFitmentPagedList,
} from "../hooks/useWsmFitmentPagedValues";
import { useWsmFitmentQualifiers, useWsmFitmentSchemas } from "../hooks/useWsmFitmentVocabulary";
import { sectionMessages } from "../messages";
import { WsmFitmentWriteResult } from "../types";
import { wsmFitmentSchemaListPath, wsmFitmentSchemaUrl } from "../urls";
import { getPickIds, getPicksToCreate, refileErrors, toWriteResult } from "../utils";

export const WsmFitmentApplicationsView = ({ schemaId }: { schemaId: string }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { schemas, loading: schemasLoading } = useWsmFitmentSchemas();
  const { qualifiers } = useWsmFitmentQualifiers();
  const { variables, bind, resetPage } = useWsmFitmentPagedList();
  const { data, loading } = useWsmFitmentApplicationsQuery({
    variables: { schema: schemaId, ...variables },
  });
  const applications = loading ? undefined : (mapEdgesToItems(data?.wsmFitmentApplications) ?? []);

  // Deleting the last row of the last page must not strand the table there.
  useWsmFitmentEmptyPageFallback(variables, applications, resetPage);

  // Every payload error below renders on the dialogs; a toast would say it twice.
  const [addApplications, addOpts] = useWsmFitmentApplicationBulkCreateMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["WsmFitmentApplications"],
  });
  const [addAxisValues] = useWsmFitmentAxisValueBulkCreateMutation({
    disableNestedErrorHandling: true,
    // A value typed into the picker is now on its axis; the next open shows it.
    refetchQueries: ["WsmFitmentAxisValues"],
  });
  const [addQualifierValues] = useWsmFitmentQualifierValueBulkCreateMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["WsmFitmentQualifiers"],
  });
  const [deleteApplication, deleteOpts] = useWsmFitmentApplicationDeleteMutation({
    disableNestedErrorHandling: true,
    refetchQueries: ["WsmFitmentApplications"],
  });
  const schema = schemas.find(candidate => candidate.id === schemaId);

  if (schemasLoading && !schema) {
    return (
      <ListPageLayout>
        <TopNav
          href={wsmFitmentSchemaListPath}
          title={intl.formatMessage(sectionMessages.applications)}
        />
        <Skeleton />
      </ListPageLayout>
    );
  }

  if (!schema) {
    return <NotFoundPage backHref={wsmFitmentSchemaListPath} />;
  }

  /**
   * A typed value the axis (or qualifier) does not have yet is created first,
   * by the idempotent bulk create, which hands back the row either way; then the
   * application is created from ids only. A resolution error names `values.0`
   * of that value's own bulk create; it is re-filed under the axes or the
   * qualifiers, whichever list it was resolving, so the dialog shows it there.
   */
  const onAdd = async (draft: WsmFitmentApplicationDraft): Promise<WsmFitmentWriteResult> => {
    const resolvedAxisValues: Record<string, string> = {};

    for (const { owner, text } of getPicksToCreate(draft.axisPicks)) {
      const result = await addAxisValues({ variables: { axis: owner, values: [text] } });
      const payload = result.data?.wsmFitmentAxisValueBulkCreate;

      if (!payload || payload.errors.length || !payload.values[0]) {
        return { saved: false, errors: refileErrors(payload?.errors ?? [], "items.0.values") };
      }

      resolvedAxisValues[owner] = payload.values[0].id;
    }

    const resolvedQualifierValues: Record<string, string> = {};

    for (const { owner, text } of getPicksToCreate(draft.qualifierPicks)) {
      const result = await addQualifierValues({ variables: { qualifier: owner, values: [text] } });
      const payload = result.data?.wsmFitmentQualifierValueBulkCreate;

      if (!payload || payload.errors.length || !payload.values[0]) {
        return {
          saved: false,
          errors: refileErrors(payload?.errors ?? [], "items.0.qualifierValues"),
        };
      }

      resolvedQualifierValues[owner] = payload.values[0].id;
    }

    const result = await addApplications({
      variables: {
        schema: schema.id,
        items: [
          {
            values: getPickIds(draft.axisPicks, resolvedAxisValues),
            qualifierValues: getPickIds(draft.qualifierPicks, resolvedQualifierValues),
          },
        ],
      },
    });
    const added = toWriteResult(result.data?.wsmFitmentApplicationBulkCreate);

    if (added.saved) {
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    }

    return added;
  };
  const onDelete = async (id: string): Promise<WsmFitmentWriteResult> => {
    const result = await deleteApplication({ variables: { id } });
    const deleted = toWriteResult(result.data?.wsmFitmentApplicationDelete);

    if (deleted.saved) {
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    }

    return deleted;
  };

  return (
    <WsmFitmentApplicationsPage
      key={wsmFitmentSchemaUrl(schema.id)}
      schema={schema}
      applications={applications}
      totalCount={data?.wsmFitmentApplications?.totalCount}
      paging={bind(data?.wsmFitmentApplications?.pageInfo)}
      qualifiers={qualifiers}
      disabled={addOpts.loading || deleteOpts.loading}
      onAdd={onAdd}
      onDelete={onDelete}
    />
  );
};

export default WsmFitmentApplicationsView;
